import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiClient } from '../packages/api-client';
import { KaziAtlasLogo } from '../components/KaziAtlasLogo';
import { SetPasswordForm, MIN_PASSWORD_LENGTH } from '../components/SetPasswordForm';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTheme } from '../hooks/useTheme';

/**
 * ResetPasswordPage — consumes a one-time token from ?token=... and lets
 * the user pick a new password. The backend marks the token used and
 * revokes any existing refresh tokens, so the user is signed out
 * everywhere and has to log in again with the new password.
 */
export function ResetPasswordPage() {
    usePageTitle('Reset password');
    const { theme } = useTheme();
    const [params] = useSearchParams();
    const token = params.get('token') ?? '';
    const navigate = useNavigate();
    const tokenMissing = token.trim() === '';

    async function handleSubmit(password: string) {
        await apiClient.post('/auth/reset-password', { token, new_password: password });
        toast.success('Password updated — please sign in with your new password.');
        navigate('/login', { replace: true });
    }

    return (
        <div className="login-container">
            <div className="login-card">
                <div style={{ marginBottom: '1rem' }}><KaziAtlasLogo size={46} onLight={theme === 'light'} /></div>

                <h1 className="login-title">Choose a new password</h1>

                {tokenMissing ? (
                    <>
                        <p className="login-subtitle" style={{ color: 'var(--danger)' }}>
                            This link is missing its reset token. Request a new one below.
                        </p>
                        <Link
                            to="/forgot-password"
                            className="btn btn-primary form-button"
                            style={{ marginTop: '1rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
                        >
                            Request new reset link
                        </Link>
                    </>
                ) : (
                    <SetPasswordForm
                        onSubmit={handleSubmit}
                        submitLabel="Update password"
                        helperText={`Your new password must be at least ${MIN_PASSWORD_LENGTH} characters. You'll be signed out of all other devices.`}
                    />
                )}
            </div>

            <p style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: '0.875rem', color: 'var(--ink-muted)' }}>
                <Link to="/login" style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>
                    Back to sign in
                </Link>
            </p>
        </div>
    );
}
